import { forwardRef, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { licenseApiService, type IssuedLicense } from '@/services/licenseApiService'

export interface ViewRequestLicenseModalRef {
  open: (requestId: string) => void
}

/**
 * Shows the license issued for a license request (BAFTT-12), so the customer
 * who asked can copy it and apply it from Plans.
 */
const ViewRequestLicenseModal = forwardRef<ViewRequestLicenseModalRef>((_, ref) => {
  const { t } = useTranslation(['requests', 'plans', 'common'])
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [license, setLicense] = useState<IssuedLicense | null>(null)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)

  useImperativeHandle(ref, () => ({
    open: (requestId: string) => {
      setLicense(null)
      setError('')
      setCopied(false)
      setIsOpen(true)
      licenseApiService
        .getRequestLicense(requestId)
        .then(setLicense)
        .catch((err) => setError(err instanceof Error ? err.message : String(err)))
    },
  }))

  const copy = async () => {
    if (!license) return
    try {
      await navigator.clipboard.writeText(license.license_key)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setError(t('plans:issueModal.errors.copyFailed'))
    }
  }

  const download = () => {
    if (!license) return
    const blob = new Blob([JSON.stringify(license, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `synstream-license-${license.reference || license.jti}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const formatDate = (iso: string) => {
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/30 backdrop-blur-md">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-primary">{t('licenseRequests.view.heading', 'Your license')}</h2>
              <p className="text-sm text-gray-600 mt-1">
                {license?.reference ?? t('licenseRequests.view.loading', 'Loading')}
              </p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label={t('common:close', 'Close')}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {!license && !error && (
            <div className="flex justify-center py-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          )}

          {license && (
            <>
              <dl className="text-sm text-gray-600 grid grid-cols-2 gap-x-4 gap-y-1">
                <dt className="font-medium text-gray-700">{t('plans:issueModal.fields.licenseType')}</dt>
                <dd>{t(`licenseRequests.type.${license.license_type}`, license.license_type)}</dd>
                <dt className="font-medium text-gray-700">{t('plans:issueModal.fields.maxFlows')}</dt>
                <dd>{license.max_flows}</dd>
                <dt className="font-medium text-gray-700">{t('licenseRequests.view.validUntil', 'Valid until')}</dt>
                <dd>{formatDate(license.expires_at)}</dd>
                <dt className="font-medium text-gray-700">{t('plans:issueModal.fields.customerName')}</dt>
                <dd className="break-words">{license.customer_name}</dd>
              </dl>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('plans:issueModal.result.keyLabel')}
                </label>
                <textarea
                  readOnly
                  value={license.license_key}
                  rows={5}
                  onFocus={(e) => e.currentTarget.select()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-xs break-all"
                />
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => void copy()}
                    className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    {copied ? t('plans:issueModal.result.copied') : t('plans:issueModal.result.copy')}
                  </button>
                  <button
                    onClick={download}
                    className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {t('plans:issueModal.result.download')}
                  </button>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  {t('licenseRequests.view.apply', 'To use it, open Plans, choose Update / Upgrade and paste this key.')}
                </p>
              </div>
            </>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('common:close', 'Close')}
          </button>
          {license && (
            <button
              type="button"
              onClick={() => {
                setIsOpen(false)
                navigate('/plans')
              }}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              {t('licenseRequests.view.openPlans', 'Open Plans')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
})

ViewRequestLicenseModal.displayName = 'ViewRequestLicenseModal'
export default ViewRequestLicenseModal
